import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

// Import benefit images
import dataRetrievalImage from '../assets/images/benefits/data-retrieval.png';
import erpIntegrationImage from '../assets/images/benefits/erp-integration.png';
import automationImage from '../assets/images/benefits/automation.png';
import analyticsImage from '../assets/images/benefits/analytics.png';
import scalabilityImage from '../assets/images/benefits/scalability.png';
import securityImage from '../assets/images/benefits/security.png';

function Benefits() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl', { alpha: true, antialias: true });
    if (!gl) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const vertexShaderSource = `
      attribute vec2 a_position;
      void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
      }
    `;

    const fragmentShaderSource = `
      precision mediump float;
      uniform vec2 u_resolution;
      uniform float u_time;
      uniform vec2 u_mouse;

      float sdCircle(vec2 p, float r) {
        return length(p) - r;
      }

      void main() {
        vec2 uv = gl_FragCoord.xy / u_resolution.xy;
        vec2 center = uv - 0.5;
        
        vec2 mouse = u_mouse / u_resolution.xy - 0.5;
        float mouseDist = length(center - mouse);
        
        vec2 orb1 = vec2(sin(u_time * 0.3) * 0.3, cos(u_time * 0.2) * 0.25);
        vec2 orb2 = vec2(cos(u_time * 0.25) * 0.35, sin(u_time * 0.35) * 0.3);
        vec2 orb3 = vec2(sin(u_time * 0.15 + 3.14) * 0.25, cos(u_time * 0.2 + 1.5) * 0.2);
        
        float d1 = sdCircle(center - orb1, 0.15);
        float d2 = sdCircle(center - orb2, 0.12);
        float d3 = sdCircle(center - orb3, 0.18);
        
        float glow1 = 0.04 / max(abs(d1), 0.01);
        float glow2 = 0.03 / max(abs(d2), 0.01);
        float glow3 = 0.05 / max(abs(d3), 0.01);
        
        float mouseGlow = 0.05 / max(mouseDist, 0.1) * 0.8;
        
        vec3 color1 = vec3(0.66, 0.33, 0.97);
        vec3 color2 = vec3(0.93, 0.28, 0.60);
        vec3 color3 = vec3(0.75, 0.45, 0.95);
        
        vec3 finalColor = color1 * glow1 + color2 * glow2 + color3 * glow3;
        finalColor += color1 * mouseGlow;
        
        float vignette = 1.0 - length(center) * 0.6;
        finalColor *= vignette;
        
        float alpha = (glow1 + glow2 + glow3 + mouseGlow) * 0.25;
        
        gl_FragColor = vec4(finalColor, alpha);
      }
    `;

    const compileShader = (source, type) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      return shader;
    };

    const vertexShader = compileShader(vertexShaderSource, gl.VERTEX_SHADER);
    const fragmentShader = compileShader(fragmentShaderSource, gl.FRAGMENT_SHADER);

    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    gl.useProgram(program);

    const positions = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    const positionLocation = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    const resolutionLocation = gl.getUniformLocation(program, 'u_resolution');
    const timeLocation = gl.getUniformLocation(program, 'u_time');
    const mouseLocation = gl.getUniformLocation(program, 'u_mouse');

    let mouseX = canvas.width / 2;
    let mouseY = canvas.height / 2;

    const handleMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = canvas.height - e.clientY;
    };

    window.addEventListener('mousemove', handleMouseMove);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    let startTime = Date.now();
    let animationId;

    const render = () => {
      const currentTime = (Date.now() - startTime) / 1000;
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
      gl.uniform1f(timeLocation, currentTime);
      gl.uniform2f(mouseLocation, mouseX, mouseY);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      animationId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationId);
      gl.deleteProgram(program);
      gl.deleteShader(vertexShader);
      gl.deleteShader(fragmentShader);
      gl.deleteBuffer(buffer);
    };
  }, []);

  return (
    <>
      {/* WebGL Canvas Background */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          pointerEvents: 'none',
          background: '#000000'
        }}
      />

      {/* Hero Header */}
      <section style={{
        background: 'transparent',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        zIndex: 1
      }}>
        <style>
          {`
            @keyframes float {
              0%, 100% { transform: translate(0, 0) rotate(0deg); }
              50% { transform: translate(-50px, 50px) rotate(180deg); }
            }
            @keyframes shimmer {
              0% { background-position: -1000px 0; }
              100% { background-position: 1000px 0; }
            }
            @keyframes slideIn {
              from { transform: translateX(-100px); opacity: 0; }
              to { transform: translateX(0); opacity: 1; }
            }
            @keyframes glow {
              0%, 100% { box-shadow: 0 0 20px rgba(168, 85, 247, 0.3), 0 0 40px rgba(168, 85, 247, 0.2); }
              50% { box-shadow: 0 0 30px rgba(168, 85, 247, 0.5), 0 0 60px rgba(236, 72, 153, 0.3); }
            }
          `}
        </style>

        {/* Backdrop blur layer for readability */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 50% 50%, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.7) 100%)',
          backdropFilter: 'blur(2px)',
          zIndex: 1
        }}></div>

        <div className="container" style={{ maxWidth: '1400px', position: 'relative', zIndex: 2 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1.3fr 0.7fr',
            gap: '5rem',
            alignItems: 'center'
          }}>
            <div>
              <div data-aos="fade-right" style={{ marginBottom: '3rem' }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '0.75rem 2rem',
                  background: 'rgba(0, 0, 0, 0.6)',
                  backdropFilter: 'blur(20px)',
                  borderLeft: '4px solid #a855f7',
                  boxShadow: '0 0 30px rgba(168, 85, 247, 0.4)',
                  border: '1px solid rgba(168, 85, 247, 0.3)'
                }}>
                  <span style={{
                    color: '#a855f7',
                    fontSize: '0.85rem',
                    fontWeight: '700',
                    fontFamily: "'Space Grotesk', sans-serif",
                    textTransform: 'uppercase',
                    letterSpacing: '2px',
                    textShadow: '0 0 20px rgba(168, 85, 247, 0.8)'
                  }}>
                    Business Benefits
                  </span>
                </div>
              </div>

              <div style={{
                background: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(15px)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid rgba(168, 85, 247, 0.1)'
              }}>
                <h1 data-aos="fade-right" data-aos-delay="100" style={{
                  fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                  color: 'white',
                  marginBottom: '2rem',
                  fontWeight: '200',
                  lineHeight: '1.1',
                  fontFamily: "'Space Grotesk', sans-serif",
                  letterSpacing: '-3px',
                  textShadow: '0 0 40px rgba(168, 85, 247, 0.5)'
                }}>
                  Transform Enterprise Operations with
                  <br />
                  <span style={{
                    display: 'inline-block',
                    marginTop: '1rem',
                    background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 50%, #a855f7 100%)',
                    backgroundSize: '200% auto',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: '300',
                    animation: 'shimmer 3s linear infinite',
                    filter: 'drop-shadow(0 0 30px rgba(168, 85, 247, 0.7))'
                  }}>
                    Intelligent AI Automation
                  </span>
                </h1>

                <p data-aos="fade-right" data-aos-delay="200" style={{
                  fontSize: '1rem',
                  color: 'rgba(255, 255, 255, 0.9)',
                  lineHeight: '1.8',
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: '300',
                  maxWidth: '700px'
                }}>
                  Achieve measurable ROI through instant data retrieval, seamless ERP integration, 
                  and enterprise-wide AI automation that scales with your business.
                </p>
              </div>
            </div>

            <div data-aos="fade-left" data-aos-delay="300" style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1.5rem'
            }}>
              {[
                { value: '90%', label: 'Faster Data Access', color: '#a855f7' },
                { value: '70%', label: 'Cost Reduction', color: '#ec4899' },
                { value: '<100ms', label: 'Query Response', color: '#a855f7' },
                { value: '24/7', label: 'Availability', color: '#ec4899' }
              ].map((metric, idx) => (
                <div key={idx} style={{
                  padding: '2rem 2.5rem',
                  background: 'rgba(0, 0, 0, 0.6)',
                  backdropFilter: 'blur(20px)',
                  borderLeft: `4px solid ${metric.color}`,
                  transition: 'all 0.3s ease',
                  animation: `slideIn 0.6s ease-out ${idx * 0.1}s backwards`,
                  boxShadow: `0 4px 30px rgba(168, 85, 247, 0.2)`,
                  border: '1px solid rgba(168, 85, 247, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)';
                  e.currentTarget.style.borderLeftWidth = '6px';
                  e.currentTarget.style.transform = 'translateX(10px)';
                  e.currentTarget.style.boxShadow = `0 8px 50px ${metric.color === '#a855f7' ? 'rgba(168, 85, 247, 0.5)' : 'rgba(236, 72, 153, 0.5)'}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
                  e.currentTarget.style.borderLeftWidth = '4px';
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.boxShadow = '0 4px 30px rgba(168, 85, 247, 0.2)';
                }}
                >
                  <div style={{
                    fontSize: '2.75rem',
                    fontWeight: '200',
                    background: `linear-gradient(135deg, ${metric.color}, #ec4899)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontFamily: "'Space Grotesk', sans-serif",
                    marginBottom: '0.5rem',
                    letterSpacing: '-1px',
                    filter: `drop-shadow(0 0 15px ${metric.color === '#a855f7' ? 'rgba(168, 85, 247, 0.6)' : 'rgba(236, 72, 153, 0.6)'})`
                  }}>
                    {metric.value}
                  </div>
                  <div style={{
                    fontSize: '1rem',
                    color: 'rgba(255, 255, 255, 0.95)',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: '400'
                  }}>
                    {metric.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section style={{ background: 'transparent', padding: '0', position: 'relative', zIndex: 1 }}>
        
        {/* Benefit 1 */}
        <div style={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid rgba(168, 85, 247, 0.2)'
        }}>
          {/* Content backdrop blur */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at 30% 50%, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.6) 100%)',
            backdropFilter: 'blur(1px)',
            zIndex: 1
          }}></div>

          <div className="container" style={{ maxWidth: '1400px', position: 'relative', zIndex: 2 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '6rem', alignItems: 'center' }}>
              <div data-aos="fade-right" style={{
                background: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(15px)',
                padding: '3rem',
                borderRadius: '16px',
                border: '1px solid rgba(168, 85, 247, 0.2)'
              }}>
                <div style={{
                  fontSize: '0.75rem',
                  color: 'rgba(168, 85, 247, 0.9)',
                  fontFamily: "'Space Grotesk', sans-serif",
                  textTransform: 'uppercase',
                  letterSpacing: '3px',
                  marginBottom: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <span style={{
                    fontSize: '3rem',
                    fontWeight: '200',
                    background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    filter: 'drop-shadow(0 0 20px rgba(168, 85, 247, 0.8))'
                  }}>01</span>
                  <div style={{ flex: 1, height: '2px', background: 'linear-gradient(90deg, rgba(168, 85, 247, 0.5) 0%, transparent 100%)' }}></div>
                </div>

                <h2 style={{
                  fontSize: 'clamp(1.5rem, 3vw, 2.5rem)',
                  color: 'white',
                  marginBottom: '2rem',
                  fontWeight: '300',
                  lineHeight: '1.2',
                  fontFamily: "'Space Grotesk', sans-serif",
                  letterSpacing: '-1px',
                  textShadow: '0 2px 20px rgba(0, 0, 0, 0.8)'
                }}>
                  Lightning-Fast Data Retrieval Across All Systems
                </h2>

                <p style={{
                  fontSize: '0.95rem',
                  color: 'rgba(255, 255, 255, 0.9)',
                  lineHeight: '1.8',
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: '300',
                  marginBottom: '2.5rem',
                  textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)'
                }}>
                  Query your entire enterprise knowledge base in milliseconds. Our AI-powered semantic search eliminates 
                  hours of manual data hunting by instantly retrieving relevant information from documents, databases, 
                  CRMs, ERPs, and cloud storage—all through natural language queries.
                </p>

                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  display: 'grid',
                  gap: '1rem',
                  fontFamily: "'Inter', sans-serif"
                }}>
                  {[
                    'Sub-100ms response times for complex multi-source queries',
                    'Natural language processing understands context and intent',
                    'Semantic search finds answers even without exact keyword matches',
                    'Unified data access across 200+ enterprise applications',
                    'Auto-citation with source attribution for audit trails'
                  ].map((item, idx) => (
                    <li key={idx} style={{
                      display: 'flex',
                      gap: '1rem',
                      alignItems: 'flex-start',
                      padding: '1rem',
                      background: 'rgba(0, 0, 0, 0.4)',
                      backdropFilter: 'blur(10px)',
                      borderLeft: '3px solid rgba(168, 85, 247, 0.5)',
                      transition: 'all 0.3s ease',
                      border: '1px solid rgba(168, 85, 247, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
                      e.currentTarget.style.borderLeftWidth = '4px';
                      e.currentTarget.style.paddingLeft = '1.5rem';
                      e.currentTarget.style.boxShadow = '0 4px 30px rgba(168, 85, 247, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(0, 0, 0, 0.4)';
                      e.currentTarget.style.borderLeftWidth = '3px';
                      e.currentTarget.style.paddingLeft = '1rem';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    >
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#a855f7',
                        marginTop: '8px',
                        flexShrink: 0,
                        boxShadow: '0 0 15px rgba(168, 85, 247, 0.8)'
                      }}></div>
                      <span style={{
                        fontSize: '0.9rem',
                        color: 'rgba(255, 255, 255, 0.95)',
                        lineHeight: '1.6',
                        textShadow: '0 1px 5px rgba(0, 0, 0, 0.5)'
                      }}>
                        <strong style={{ color: 'white' }}>{item.split(' ')[0]}</strong> {item.substring(item.indexOf(' ') + 1)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div data-aos="fade-left" data-aos-delay="200" style={{ position: 'relative' }}>
                <img 
                  src={dataRetrievalImage} 
                  alt="AI-Powered Data Retrieval Dashboard" 
                  style={{
                    width: '100%',
                    borderRadius: '12px',
                    border: '2px solid rgba(168, 85, 247, 0.4)',
                    boxShadow: '0 30px 80px rgba(168, 85, 247, 0.4), 0 0 60px rgba(168, 85, 247, 0.3)'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Benefit 2 */}
        <div style={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid rgba(168, 85, 247, 0.2)'
        }}>
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at 70% 50%, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.6) 100%)',
            backdropFilter: 'blur(1px)',
            zIndex: 1
          }}></div>

          <div className="container" style={{ maxWidth: '1400px', position: 'relative', zIndex: 2 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '0.8fr 1.2fr', gap: '6rem', alignItems: 'center' }}>
              <div data-aos="fade-right" data-aos-delay="200" style={{ position: 'relative' }}>
                <img 
                  src={erpIntegrationImage} 
                  alt="ERP Integration Architecture Diagram" 
                  style={{
                    width: '100%',
                    borderRadius: '12px',
                    border: '2px solid rgba(236, 72, 153, 0.4)',
                    boxShadow: '0 30px 80px rgba(236, 72, 153, 0.4), 0 0 60px rgba(236, 72, 153, 0.3)'
                  }}
                />
              </div>

              <div data-aos="fade-left" style={{
                background: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(15px)',
                padding: '3rem',
                borderRadius: '16px',
                border: '1px solid rgba(236, 72, 153, 0.2)'
              }}>
                <div style={{
                  fontSize: '0.75rem',
                  color: 'rgba(236, 72, 153, 0.9)',
                  fontFamily: "'Space Grotesk', sans-serif",
                  textTransform: 'uppercase',
                  letterSpacing: '3px',
                  marginBottom: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <span style={{
                    fontSize: '3rem',
                    fontWeight: '200',
                    background: 'linear-gradient(135deg, #ec4899, #a855f7)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    filter: 'drop-shadow(0 0 20px rgba(236, 72, 153, 0.8))'
                  }}>02</span>
                  <div style={{ flex: 1, height: '2px', background: 'linear-gradient(90deg, rgba(236, 72, 153, 0.5) 0%, transparent 100%)' }}></div>
                </div>

                <h2 style={{
                  fontSize: 'clamp(2rem, 4vw, 3.5rem)',
                  color: 'white',
                  marginBottom: '2rem',
                  fontWeight: '300',
                  lineHeight: '1.2',
                  fontFamily: "'Space Grotesk', sans-serif",
                  letterSpacing: '-1px',
                  textShadow: '0 2px 20px rgba(0, 0, 0, 0.8)'
                }}>
                  Seamless ERP Integration with Real-Time API Synchronization
                </h2>

                <p style={{
                  fontSize: '1.15rem',
                  color: 'rgba(255, 255, 255, 0.9)',
                  lineHeight: '1.8',
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: '300',
                  marginBottom: '2.5rem',
                  textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)'
                }}>
                  Connect your AI assistant directly to SAP, Oracle, NetSuite, Microsoft Dynamics, and custom ERPs through 
                  secure APIs. Enable instant data pushing, automated workflows, and bidirectional synchronization that 
                  keeps your entire tech stack aligned in real-time.
                </p>

                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  display: 'grid',
                  gap: '1rem',
                  fontFamily: "'Inter', sans-serif"
                }}>
                  {[
                    'REST & GraphQL APIs for flexible integration architecture',
                    'Real-time data sync with sub-second latency',
                    'Automated workflows trigger actions across connected systems',
                    'Order-to-cash automation from quote to revenue recognition',
                    'Inventory management with live stock level updates',
                    'Financial close acceleration through automated reconciliation'
                  ].map((item, idx) => (
                    <li key={idx} style={{
                      display: 'flex',
                      gap: '1rem',
                      alignItems: 'flex-start',
                      padding: '1rem',
                      background: 'rgba(0, 0, 0, 0.4)',
                      backdropFilter: 'blur(10px)',
                      borderLeft: '3px solid rgba(236, 72, 153, 0.5)',
                      transition: 'all 0.3s ease',
                      border: '1px solid rgba(236, 72, 153, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
                      e.currentTarget.style.borderLeftWidth = '4px';
                      e.currentTarget.style.paddingLeft = '1.5rem';
                      e.currentTarget.style.boxShadow = '0 4px 30px rgba(236, 72, 153, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(0, 0, 0, 0.4)';
                      e.currentTarget.style.borderLeftWidth = '3px';
                      e.currentTarget.style.paddingLeft = '1rem';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    >
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#ec4899',
                        marginTop: '8px',
                        flexShrink: 0,
                        boxShadow: '0 0 15px rgba(236, 72, 153, 0.8)'
                      }}></div>
                      <span style={{
                        fontSize: '1rem',
                        color: 'rgba(255, 255, 255, 0.95)',
                        lineHeight: '1.6',
                        textShadow: '0 1px 5px rgba(0, 0, 0, 0.5)'
                      }}>
                        <strong style={{ color: 'white' }}>{item.split(' ')[0]} {item.split(' ')[1] || ''}</strong> {item.substring(item.indexOf(' ', item.indexOf(' ') + 1) + 1)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits 3, 4, 5, 6 - Same pattern with backdrop blur containers */}
        {[
          { num: '03', title: 'End-to-End Process Automation That Scales', img: automationImage, color: '#a855f7', items: [
            'Document processing automation with 99.2% accuracy (OCR, NLP, classification)',
            'Automated email responses handling 70% of routine inquiries',
            'Invoice & expense automation reducing processing time from days to minutes',
            'Smart routing of tasks to appropriate teams based on content analysis',
            'Compliance monitoring with automated policy enforcement',
            'RPA integration for legacy system automation'
          ], desc: 'Automate repetitive tasks across departments with AI-driven workflows. From invoice processing to customer onboarding, our platform eliminates manual handoffs, reduces errors by 92%, and processes 10x more transactions without additional headcount.', reverse: false },
          { num: '04', title: 'Predictive Analytics for Data-Driven Decision Making', img: analyticsImage, color: '#ec4899', items: [
            'Demand forecasting reducing inventory costs by 30%',
            'Customer churn prediction with 85% accuracy',
            'Sales pipeline intelligence identifying high-value opportunities',
            'Cash flow forecasting with scenario modeling',
            'Anomaly detection for fraud prevention and quality control',
            'Custom BI dashboards with natural language query generation'
          ], desc: 'Transform historical data into actionable insights with AI-powered forecasting, trend analysis, and anomaly detection. Make informed decisions 10x faster with real-time dashboards that aggregate data from every corner of your enterprise.', reverse: true },
          { num: '05', title: 'Scale Operations Infinitely Without Adding Headcount', img: scalabilityImage, color: '#a855f7', items: [
            'Kubernetes-native deployment with auto-scaling capabilities',
            'Zero marginal cost per query after initial deployment',
            'Multi-region redundancy ensuring 99.9% uptime SLA',
            'Load balancing across distributed infrastructure',
            'Unlimited concurrent users with consistent performance',
            'Elastic compute resources optimizing infrastructure costs'
          ], desc: 'Handle 100x more queries and transactions as your business grows—without proportional increases in staff. Our cloud-native architecture auto-scales to demand, maintaining sub-100ms response times even during peak loads while keeping operational costs predictable.', reverse: false },
          { num: '06', title: 'Military-Grade Security with Complete Data Sovereignty', img: securityImage, color: '#ec4899', items: [
            'AES-256 encryption at rest and TLS 1.3 in transit',
            'On-premise deployment for complete data sovereignty',
            'Role-based access control with granular permissions',
            'SSO integration (Okta, Azure AD, Google Workspace)',
            'Automated compliance reporting for GDPR, HIPAA, SOC 2',
            'Immutable audit logs with tamper-proof blockchain verification',
            'Data residency controls meeting regional compliance requirements'
          ], desc: 'Your data remains under your complete control with on-premise, VPC, or hybrid deployment options. Built-in compliance frameworks (GDPR, SOC 2, HIPAA, ISO 27001) with end-to-end encryption, RBAC, SSO integration, and comprehensive audit trails ensure zero-trust security.', reverse: true }
        ].map((benefit, idx) => (
          <div key={idx} style={{
            position: 'relative',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            borderBottom: idx === 3 ? 'none' : '1px solid rgba(168, 85, 247, 0.2)'
          }}>
            <div style={{
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(circle at ${benefit.reverse ? '70%' : '30%'} 50%, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.6) 100%)`,
              backdropFilter: 'blur(1px)',
              zIndex: 1
            }}></div>

            <div className="container" style={{ maxWidth: '1400px', position: 'relative', zIndex: 2 }}>
              <div style={{ display: 'grid', gridTemplateColumns: benefit.reverse ? '0.8fr 1.2fr' : '1.2fr 0.8fr', gap: '6rem', alignItems: 'center' }}>
                {benefit.reverse && (
                  <div data-aos="fade-right" data-aos-delay="200">
                    <img src={benefit.img} alt={benefit.title} style={{
                      width: '100%',
                      borderRadius: '12px',
                      border: `2px solid ${benefit.color === '#a855f7' ? 'rgba(168, 85, 247, 0.4)' : 'rgba(236, 72, 153, 0.4)'}`,
                      boxShadow: `0 30px 80px ${benefit.color === '#a855f7' ? 'rgba(168, 85, 247, 0.4)' : 'rgba(236, 72, 153, 0.4)'}, 0 0 60px ${benefit.color === '#a855f7' ? 'rgba(168, 85, 247, 0.3)' : 'rgba(236, 72, 153, 0.3)'}`
                    }} />
                  </div>
                )}

                <div data-aos={benefit.reverse ? "fade-left" : "fade-right"} style={{
                  background: 'rgba(0, 0, 0, 0.5)',
                  backdropFilter: 'blur(15px)',
                  padding: '3rem',
                  borderRadius: '16px',
                  border: `1px solid ${benefit.color === '#a855f7' ? 'rgba(168, 85, 247, 0.2)' : 'rgba(236, 72, 153, 0.2)'}`
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: benefit.color === '#a855f7' ? 'rgba(168, 85, 247, 0.9)' : 'rgba(236, 72, 153, 0.9)',
                    fontFamily: "'Space Grotesk', sans-serif",
                    textTransform: 'uppercase',
                    letterSpacing: '3px',
                    marginBottom: '2rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                  }}>
                    <span style={{
                      fontSize: '3rem',
                      fontWeight: '200',
                      background: `linear-gradient(135deg, ${benefit.color}, ${benefit.color === '#a855f7' ? '#ec4899' : '#a855f7'})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      filter: `drop-shadow(0 0 20px ${benefit.color === '#a855f7' ? 'rgba(168, 85, 247, 0.8)' : 'rgba(236, 72, 153, 0.8)'})`
                    }}>{benefit.num}</span>
                    <div style={{ flex: 1, height: '2px', background: `linear-gradient(90deg, ${benefit.color === '#a855f7' ? 'rgba(168, 85, 247, 0.5)' : 'rgba(236, 72, 153, 0.5)'} 0%, transparent 100%)` }}></div>
                  </div>

                  <h2 style={{
                    fontSize: 'clamp(2rem, 4vw, 3.5rem)',
                    color: 'white',
                    marginBottom: '2rem',
                    fontWeight: '300',
                    lineHeight: '1.2',
                    fontFamily: "'Space Grotesk', sans-serif",
                    letterSpacing: '-1px',
                    textShadow: '0 2px 20px rgba(0, 0, 0, 0.8)'
                  }}>
                    {benefit.title}
                  </h2>

                  <p style={{
                    fontSize: '1.15rem',
                    color: 'rgba(255, 255, 255, 0.9)',
                    lineHeight: '1.8',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: '300',
                    marginBottom: '2.5rem',
                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)'
                  }}>
                    {benefit.desc}
                  </p>

                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    display: 'grid',
                    gap: '1rem',
                    fontFamily: "'Inter', sans-serif"
                  }}>
                    {benefit.items.map((item, i) => (
                      <li key={i} style={{
                        display: 'flex',
                        gap: '1rem',
                        alignItems: 'flex-start',
                        padding: '1rem',
                        background: 'rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(10px)',
                        borderLeft: `3px solid ${benefit.color === '#a855f7' ? 'rgba(168, 85, 247, 0.5)' : 'rgba(236, 72, 153, 0.5)'}`,
                        transition: 'all 0.3s ease',
                        border: `1px solid ${benefit.color === '#a855f7' ? 'rgba(168, 85, 247, 0.1)' : 'rgba(236, 72, 153, 0.1)'}`
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
                        e.currentTarget.style.borderLeftWidth = '4px';
                        e.currentTarget.style.paddingLeft = '1.5rem';
                        e.currentTarget.style.boxShadow = `0 4px 30px ${benefit.color === '#a855f7' ? 'rgba(168, 85, 247, 0.3)' : 'rgba(236, 72, 153, 0.3)'}`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.4)';
                        e.currentTarget.style.borderLeftWidth = '3px';
                        e.currentTarget.style.paddingLeft = '1rem';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      >
                        <div style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: benefit.color,
                          marginTop: '8px',
                          flexShrink: 0,
                          boxShadow: `0 0 15px ${benefit.color === '#a855f7' ? 'rgba(168, 85, 247, 0.8)' : 'rgba(236, 72, 153, 0.8)'}`
                        }}></div>
                        <span style={{
                          fontSize: '1rem',
                          color: 'rgba(255, 255, 255, 0.95)',
                          lineHeight: '1.6',
                          textShadow: '0 1px 5px rgba(0, 0, 0, 0.5)'
                        }}>
                          <strong style={{ color: 'white' }}>{item.split(' ')[0]} {item.split(' ')[1] || ''}</strong> {item.substring(item.indexOf(' ', item.indexOf(' ') + 1) + 1)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {!benefit.reverse && (
                  <div data-aos="fade-left" data-aos-delay="200">
                    <img src={benefit.img} alt={benefit.title} style={{
                      width: '100%',
                      borderRadius: '12px',
                      border: `2px solid ${benefit.color === '#a855f7' ? 'rgba(168, 85, 247, 0.4)' : 'rgba(236, 72, 153, 0.4)'}`,
                      boxShadow: `0 30px 80px ${benefit.color === '#a855f7' ? 'rgba(168, 85, 247, 0.4)' : 'rgba(236, 72, 153, 0.4)'}, 0 0 60px ${benefit.color === '#a855f7' ? 'rgba(168, 85, 247, 0.3)' : 'rgba(236, 72, 153, 0.3)'}`
                    }} />
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Stats Section */}
      <section style={{
        padding: '10rem 0',
        background: 'transparent',
        position: 'relative',
        overflow: 'hidden',
        zIndex: 1
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 50% 50%, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.7) 100%)',
          backdropFilter: 'blur(2px)',
          zIndex: 1
        }}></div>

        <div className="container" style={{ maxWidth: '1400px', position: 'relative', zIndex: 2 }}>
          <div style={{ textAlign: 'center', marginBottom: '6rem' }}>
            <div data-aos="fade-up" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '0.75rem 2rem',
              background: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(20px)',
              borderLeft: '4px solid #a855f7',
              marginBottom: '2rem',
              boxShadow: '0 0 30px rgba(168, 85, 247, 0.4)',
              border: '1px solid rgba(168, 85, 247, 0.3)'
            }}>
              <span style={{
                color: '#a855f7',
                fontSize: '0.85rem',
                fontWeight: '700',
                fontFamily: "'Space Grotesk', sans-serif",
                textTransform: 'uppercase',
                letterSpacing: '2px',
                textShadow: '0 0 20px rgba(168, 85, 247, 0.8)'
              }}>
                Proven Results
              </span>
            </div>

            <div style={{
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(15px)',
              padding: '2rem',
              borderRadius: '12px',
              display: 'inline-block',
              border: '1px solid rgba(168, 85, 247, 0.1)'
            }}>
              <h2 data-aos="fade-up" data-aos-delay="100" style={{
                fontSize: 'clamp(1.8rem, 4vw, 3rem)',
                color: 'white',
                marginBottom: '1rem',
                fontWeight: '200',
                fontFamily: "'Space Grotesk', sans-serif",
                letterSpacing: '-2px',
                textShadow: '0 2px 20px rgba(0, 0, 0, 0.8)'
              }}>
                Measurable ROI Across Industries
              </h2>

              <p data-aos="fade-up" data-aos-delay="200" style={{
                fontSize: '1rem',
                color: 'rgba(255, 255, 255, 0.8)',
                fontFamily: "'Inter', sans-serif",
                fontWeight: '300'
              }}>
                Leading enterprises trust our platform to deliver tangible business value
              </p>
            </div>
          </div>

          <div data-aos="fade-up" data-aos-delay="300" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '2rem'
          }}>
            {[
              { number: '40-60min', label: 'Daily Time Saved Per Employee', desc: 'Enterprise users report significant productivity gains', color: '#a855f7' },
              { number: '70%', label: 'Reduction in Training Costs', desc: 'Accelerated onboarding from weeks to days', color: '#ec4899' },
              { number: '90%', label: 'Faster Information Access', desc: 'Sub-second retrieval across all enterprise systems', color: '#a855f7' },
              { number: '92%', label: 'Error Reduction Rate', desc: 'Automated processes eliminate manual mistakes', color: '#ec4899' },
              { number: '99.9%', label: 'Platform Uptime SLA', desc: 'Enterprise-grade reliability and availability', color: '#a855f7' },
              { number: '10x', label: 'Transaction Volume Increase', desc: 'Scale without proportional cost growth', color: '#ec4899' }
            ].map((stat, idx) => (
              <div key={idx} style={{
                padding: '3rem',
                background: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(20px)',
                border: `2px solid ${stat.color === '#a855f7' ? 'rgba(168, 85, 247, 0.3)' : 'rgba(236, 72, 153, 0.3)'}`,
                transition: 'all 0.4s ease',
                borderRadius: '12px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)';
                e.currentTarget.style.borderColor = stat.color;
                e.currentTarget.style.transform = 'translateY(-10px)';
                e.currentTarget.style.boxShadow = `0 20px 60px ${stat.color === '#a855f7' ? 'rgba(168, 85, 247, 0.5)' : 'rgba(236, 72, 153, 0.5)'}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
                e.currentTarget.style.borderColor = `${stat.color === '#a855f7' ? 'rgba(168, 85, 247, 0.3)' : 'rgba(236, 72, 153, 0.3)'}`;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              >
                <div style={{
                  fontSize: '3.5rem',
                  fontWeight: '200',
                  background: `linear-gradient(135deg, ${stat.color}, #ec4899)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '1rem',
                  fontFamily: "'Space Grotesk', sans-serif",
                  letterSpacing: '-2px',
                  filter: `drop-shadow(0 0 15px ${stat.color === '#a855f7' ? 'rgba(168, 85, 247, 0.6)' : 'rgba(236, 72, 153, 0.6)'})`
                }}>
                  {stat.number}
                </div>

                <div style={{
                  fontSize: '1rem',
                  color: 'white',
                  marginBottom: '0.75rem',
                  fontWeight: '400',
                  fontFamily: "'Space Grotesk', sans-serif",
                  lineHeight: '1.3',
                  textShadow: '0 1px 5px rgba(0, 0, 0, 0.5)'
                }}>
                  {stat.label}
                </div>

                <div style={{
                  fontSize: '0.85rem',
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontFamily: "'Inter', sans-serif",
                  lineHeight: '1.6',
                  textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)'
                }}>
                  {stat.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '10rem 0',
        background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
        position: 'relative',
        overflow: 'hidden',
        zIndex: 1
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.15) 0%, transparent 70%)',
          pointerEvents: 'none'
        }}></div>

        <div className="container" style={{ maxWidth: '1000px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <h2 data-aos="fade-up" style={{
            fontSize: 'clamp(1.8rem, 5vw, 3.5rem)',
            color: 'white',
            marginBottom: '2rem',
            fontWeight: '200',
            lineHeight: '1.1',
            fontFamily: "'Space Grotesk', sans-serif",
            letterSpacing: '-2px',
            textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}>
            Ready to Transform Your Enterprise?
          </h2>

          <p data-aos="fade-up" data-aos-delay="100" style={{
            fontSize: '1.1rem',
            color: 'rgba(255, 255, 255, 0.95)',
            maxWidth: '800px',
            margin: '0 auto 4rem',
            lineHeight: '1.7',
            fontFamily: "'Inter', sans-serif",
            fontWeight: '300',
            textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)'
          }}>
            Join industry leaders who have achieved 10x productivity gains and 70% cost reductions. 
            Schedule a personalized demo and see your ROI projection in 30 days.
          </p>

          <div data-aos="fade-up" data-aos-delay="200" style={{
            display: 'flex',
            gap: '2rem',
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginBottom: '4rem'
          }}>
            <Link to="/contact" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1.5rem 3.5rem',
              background: 'white',
              color: '#a855f7',
              textDecoration: 'none',
              fontSize: '1rem',
              fontWeight: '600',
              fontFamily: "'Space Grotesk', sans-serif",
              borderRadius: '0',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 25px 80px rgba(0, 0, 0, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.3)';
            }}
            >
              Schedule Your Demo
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>

            <Link to="/pricing" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1.5rem 3.5rem',
              background: 'transparent',
              color: 'white',
              textDecoration: 'none',
              fontSize: '1rem',
              fontWeight: '600',
              fontFamily: "'Space Grotesk', sans-serif",
              borderRadius: '0',
              border: '2px solid white',
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(10px)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.color = '#a855f7';
              e.currentTarget.style.transform = 'translateY(-5px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            >
              View Pricing
            </Link>
          </div>

          <div style={{
            fontSize: '1.05rem',
            color: 'rgba(255, 255, 255, 0.95)',
            fontFamily: "'Inter', sans-serif",
            display: 'flex',
            justifyContent: 'center',
            gap: '2rem',
            flexWrap: 'wrap',
            textShadow: '0 2px 5px rgba(0, 0, 0, 0.2)'
          }}>
            <span>✓ Free 14-day trial</span>
            <span>•</span>
            <span>✓ No credit card required</span>
            <span>•</span>
            <span>✓ Deploy in under 1 hour</span>
          </div>
        </div>
      </section>
    </>
  );
}

export default Benefits;
